import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { cn, getMediaStreamingUrl } from "@akina/utils";

const EXERCISE_PREVIEW_LOAD_DEBOUNCE = 150;

export function ExerciseThumbnail({
    thumbnail,
    variant = "vertical",
    previewGif,
}: Readonly<{
    thumbnail?: string;
    variant?: "horizontal" | "vertical";
    previewGif?: string;
}>) {
    const hasThumbnail = Boolean(thumbnail);
    const hasPreview = Boolean(previewGif) && hasThumbnail;
    const rounded = variant === "horizontal" ? "rounded-l-xl" : "rounded-t-xl";
    const baseImageClasses = "absolute inset-0 h-full w-full! object-cover";

    const [isHovering, setIsHovering] = useState(false);
    const [previewMounted, setPreviewMounted] = useState(false);
    const [isPreviewLoaded, setIsPreviewLoaded] = useState(false);

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const handleMouseEnter = useCallback(() => {
        setIsHovering(true);
        if (timerRef.current) return;
        timerRef.current = setTimeout(() => {
            setPreviewMounted(true);
        }, EXERCISE_PREVIEW_LOAD_DEBOUNCE);
    }, []);

    const handleMouseLeave = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        setIsHovering(false);
        setPreviewMounted(false);
        setIsPreviewLoaded(false);
    }, []);

    const urls = useMemo(
        () => ({
            thumbnail: thumbnail ? getMediaStreamingUrl(thumbnail) : undefined,
            previewGif: previewGif ? getMediaStreamingUrl(previewGif) : undefined,
        }),
        [thumbnail, previewGif],
    );

    const shouldAnimate = isPreviewLoaded && isHovering;
    const transitionClass = shouldAnimate ? "transition-opacity duration-600" : "transition-opacity duration-0";

    const showPreview = Boolean(hasPreview && previewMounted && urls.previewGif);

    return (
        <div
            role="img"
            aria-label="Exercise thumbnail"
            className={twMerge("group relative h-full w-full overflow-hidden", rounded)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {hasPreview ? <div className="absolute inset-0 h-full w-full bg-gray-500" /> : null}
            {hasThumbnail && urls.thumbnail ? (
                <div className="relative h-full w-full">
                    <img
                        src={urls.thumbnail}
                        alt="exercise thumbnail"
                        className={cn(baseImageClasses, shouldAnimate ? "opacity-0" : "opacity-100", transitionClass)}
                        draggable={false}
                    />
                    {showPreview ? (
                        <img
                            src={urls.previewGif}
                            alt=""
                            className={cn(baseImageClasses, shouldAnimate ? "opacity-100" : "opacity-0", transitionClass)}
                            draggable={false}
                            onLoad={() => setIsPreviewLoaded(true)}
                        />
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
